import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { PivotData, getSort, sortAs } from './Utilities';
import PivotTable from './PivotTable';
import Sortable from 'sortablejs';
import { Loader2 } from 'lucide-react';

// Helper to unwrap ESM modules or other object wrappers
const normalize = (obj: any) => {
    if (!obj) return obj;
    if (obj.__esModule && obj.default) return obj.default;
    if (typeof obj === 'object' && obj.default && (typeof obj.default === 'function' || (typeof obj.default === 'object' && obj.default.render))) {
        return obj.default;
    }
    return obj;
};

// Components
const PivotTableComponent = PivotTable;

class SortableJSWrapper extends React.Component<any> {
    containerRef: React.RefObject<HTMLElement>;
    sortableInstance: Sortable | null = null;

    constructor(props: any) {
        super(props);
        this.containerRef = React.createRef();
    }

    override componentDidMount() {
        if (this.containerRef.current) {
            const onChanged = () => {
                if (this.sortableInstance && this.props['onChange']) {
                    this.props['onChange'](this.sortableInstance.toArray());
                }
            };

            this.sortableInstance = Sortable.create(this.containerRef.current, {
                ...this.props['options'],
                onAdd: onChanged,
                onUpdate: onChanged,
                onRemove: () => {
                    // Ignore remove events to prevent race conditions with the destination list
                    // The destination list will handle the state update (pull model)
                }
            });
        }
    }

    override componentWillUnmount() {
        if (this.sortableInstance) {
            this.sortableInstance.destroy();
            this.sortableInstance = null;
        }
    }

    // We don't need to manually update Sortable instance on prop change usually
    // because React will re-render the children, and Sortable respects that (mostly).
    // But if options change dynamically, we might need code here. For now, static options.

    override render() {
        const { tag: Tag = 'div', className, style, children } = this.props;
        return (
            <Tag ref={this.containerRef} className={className} style={style}>
                {children}
            </Tag>
        );
    }
}



interface DraggableAttributeProps {
    name: string;
    addValuesToFilter: (attr: string, values: any[]) => void;
    removeValuesFromFilter: (attr: string, values: any[]) => void;
    setValuesInFilter: (attr: string, values: any[]) => void;
    attrValues: Record<string, number>;
    valueFilter: Record<string, boolean>;
    moveFilterBoxToTop: (attr: string) => void;
    sorter: (a: any, b: any) => number;
    menuLimit?: number | null;
    zIndex?: number;
    groupByHour?: boolean | undefined;
    toggleGroupByHour?: ((val: boolean) => void) | undefined;
    sortDir?: 'asc' | 'desc' | 'none' | undefined;
    toggleSortDir?: ((dir: 'asc' | 'desc') => void) | undefined;
    isMergeActive?: boolean;
    toggleMerge?: ((val: boolean) => void);
}

interface DraggableAttributeState {
    open: boolean;
    filterText: string;
    displayLimit: number;
}

export class DraggableAttribute extends React.Component<DraggableAttributeProps, DraggableAttributeState> {
    filterBoxRef: React.RefObject<HTMLDivElement>;
    attrRef: React.RefObject<HTMLDivElement>;
    sortedValues: string[] = [];
    prevAttrValues: Record<string, number> | null = null;

    constructor(props: DraggableAttributeProps) {
        super(props);
        this.state = {
            open: false,
            filterText: '',
            displayLimit: 100, // Default limit for filter values
        };
        this.filterBoxRef = React.createRef<HTMLDivElement>();
        this.attrRef = React.createRef<HTMLDivElement>();
    }

    override componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    override componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    // Handle clicks outside the filter box to close it
    handleClickOutside = (event: MouseEvent) => {
        if (
            this.state.open &&
            this.filterBoxRef.current &&
            !this.filterBoxRef.current.contains(event.target as Node) &&
            this.attrRef.current &&
            !this.attrRef.current.contains(event.target as Node)
        ) {
            this.setState({ open: false });
        }
    };

    getSortedValues(): string[] {
        if (this.sortedValues && this.sortedValues.length > 0 && this.prevAttrValues === this.props.attrValues) {
            return this.sortedValues;
        }
        let values = Object.keys(this.props.attrValues);
        if (this.props.name === 'HAKA HAKI') {
            values = ['HAKA', 'HAKI', 'ALL'];
        }
        this.sortedValues = values.sort(this.props.sorter);
        this.prevAttrValues = this.props.attrValues;
        return this.sortedValues;
    }

    toggleValue(value: string) {
        if (this.props.name === 'HAKA HAKI') {
            const nextFilter = { ...(this.props.valueFilter || {}) };
            
            if (value === 'ALL') {
                if ('ALL' in nextFilter) {
                    delete nextFilter['ALL'];
                    nextFilter['HAKA'] = true;
                    nextFilter['HAKI'] = true;
                } else {
                    nextFilter['ALL'] = true;
                    delete nextFilter['HAKA'];
                    delete nextFilter['HAKI'];
                }
            } else {
                nextFilter['ALL'] = true;
                if (value in nextFilter) {
                    delete nextFilter[value]; 
                } else {
                    nextFilter[value] = true; 
                }
            }
            this.props.setValuesInFilter(this.props.name, Object.keys(nextFilter));
            return;
        }

        if (value in this.props.valueFilter) {
            this.props.removeValuesFromFilter(this.props.name, [value]);
        } else {
            this.props.addValuesToFilter(this.props.name, [value]);
        }
    }

    selectOnly(e: React.MouseEvent, value: string) {
        e.stopPropagation();
        this.props.setValuesInFilter(
            this.props.name,
            Object.keys(this.props.attrValues).filter(y => y !== value)
        );
    }

    getFilterBox() {
        const showMenu = this.state.open;
        if (!showMenu) {
            return null;
        }

        const sortedValues = this.getSortedValues();
        const rawFilterText = this.state.filterText || '';
        const filterText = rawFilterText.toLowerCase().trim();

        let shown = sortedValues;
        if (filterText) {
            shown = sortedValues.filter(x => 
                String(x).toLowerCase().includes(filterText)
            );
        }

        return (
            <div
                className="pvtFilterBox"
                ref={this.filterBoxRef}
                style={{
                    display: 'block',
                    cursor: 'initial',
                    zIndex: 5000,
                }}
                onClick={(e) => {
                    e.stopPropagation(); // Avoid toggling when clicking inside the box
                }}
            >
                <div className="pvtSearchContainer">
                    <p>{this.props.name}</p>
                    <input
                        type="text"
                        placeholder="Filter values"
                        className="pvtSearch"
                        value={this.state.filterText}
                        onChange={e =>
                            this.setState({
                                filterText: e.target.value,
                            })
                        }
                    />
                    {this.props.groupByHour !== undefined && (
                        <div className="flex items-center gap-1.5 mt-2 mb-1" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={this.props.groupByHour}
                                onChange={(e) => this.props.toggleGroupByHour?.(e.target.checked)}
                                className="rounded border-gray-300 w-3 h-3 text-primary focus:ring-primary"
                                id={`group-hour-${this.props.name}`}
                            />
                            <label
                                htmlFor={`group-hour-${this.props.name}`}
                                className="text-[10px] text-muted-foreground cursor-pointer select-none"
                            >
                                Group by Hour
                            </label>
                        </div>
                    )}
                    {this.props.toggleMerge !== undefined && (
                        <div className="flex items-center gap-1.5 mt-2 mb-1" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={this.props.isMergeActive}
                                onChange={(e) => this.props.toggleMerge?.(e.target.checked)}
                                className="rounded border-gray-300 w-3 h-3 text-primary focus:ring-primary"
                                id={`merge-active-${this.props.name}`}
                            />
                            <label
                                htmlFor={`merge-active-${this.props.name}`}
                                className="text-[10px] text-muted-foreground cursor-pointer select-none"
                            >
                                Merge
                            </label>
                        </div>
                    )}
                    {this.props.sortDir !== undefined && (
                        <div className="flex items-center gap-2 mt-2 mb-1" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[10px] text-muted-foreground select-none">Sort:</span>
                            <div className="flex bg-gray-100 rounded p-0.5">
                                <button
                                    onClick={() => this.props.toggleSortDir?.('asc')}
                                    className={`text-[9px] px-1.5 py-0.5 rounded ${this.props.sortDir === 'asc' ? 'bg-white shadow-sm font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    ASC
                                </button>
                                <button
                                    onClick={() => this.props.toggleSortDir?.('desc')}
                                    className={`text-[9px] px-1.5 py-0.5 rounded ${this.props.sortDir === 'desc' ? 'bg-white shadow-sm font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    DESC
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div
                    className="pvtCheckContainer"
                    style={{
                        maxHeight: '250px',
                        overflowY: 'auto',
                        borderBottom: '1px solid #ddd'
                    }}
                    onScroll={(e: any) => {
                        const el = e.target;
                        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
                            if (this.state.displayLimit < shown.length) {
                                this.setState({ displayLimit: this.state.displayLimit + 100 });
                            }
                        }
                    }}
                >
                    {shown.slice(0, this.state.displayLimit).map(x => ( // Use displayLimit
                        <p
                            key={x}
                            onClick={() => this.toggleValue(x)}
                            className={x in this.props.valueFilter ? '' : 'selected'}
                        >
                            <a className="pvtOnly" onClick={e => this.selectOnly(e, x)}>
                                only
                            </a>
                            <a className="pvtOnlySpacer">&nbsp;</a>
                            {x === '' ? <em>null</em> : x}
                        </p>
                    ))}
                    {shown.length > this.state.displayLimit && (
                        <p className="pvtMore" style={{ textAlign: 'center', color: '#888' }}>
                            ...and {shown.length - this.state.displayLimit} more... (Scroll to load)
                        </p>
                    )}
                </div>
                <div className="pvtFilterBoxButtons" style={{ padding: '5px', textAlign: 'center' }}>
                    <button
                        onClick={() => {
                            if (this.props.name === 'HAKA HAKI') {
                                this.props.setValuesInFilter(this.props.name, ['ALL']);
                            } else {
                                this.props.setValuesInFilter(this.props.name, []);
                            }
                        }}
                        style={{ marginRight: '5px', cursor: 'pointer', padding: '2px 5px', color: 'white' }}
                    >
                        Select All
                    </button>
                    <button
                        onClick={() => {
                            if (this.props.name === 'HAKA HAKI') {
                                this.props.setValuesInFilter(this.props.name, ['HAKA', 'HAKI', 'ALL']);
                            } else {
                                this.props.addValuesToFilter(this.props.name, Object.keys(this.props.attrValues));
                            }
                        }}
                        style={{ cursor: 'pointer', padding: '2px 5px', color: 'white' }}
                    >
                        Deselect All
                    </button>
                </div>
            </div>
        );
    }

    toggleFilter(e: any) {
        e.stopPropagation();
        this.setState({ open: !this.state.open });
    }

    override render() {
        if (this.props.name === 'VOLUME') return null;
        
        const valueFilterKeys = Object.keys(this.props.valueFilter || {});
        let isModified = false;
        if (this.props.name === 'HAKA HAKI') {
            // Default for HAKA HAKI is exactly { ALL: true }
            isModified = !(valueFilterKeys.length === 1 && this.props.valueFilter['ALL'] === true);
        } else {
            isModified = valueFilterKeys.length !== 0;
        }

        const filtered =
            Object.keys(this.props.valueFilter || {}).length !== 0
                ? 'pvtFilteredAttribute'
                : '';

        // Compute classes conditionally
        const baseClasses = `pvtAttr ${filtered} `;
        // If sorting or other features need classes, add them here.
        // For now, we will add 'cursor-pointer' or similar if needed,
        // but react-sortablejs handles the drag handle.

        return (
            <div
                className={baseClasses}
                data-id={this.props.name} // Important for SortableJS
                ref={this.attrRef}
                onClick={this.toggleFilter.bind(this)}
                style={{ cursor: 'pointer', position: 'relative' }}
            >
                {this.props.name}
                {isModified && (
                    <span 
                        className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400 shadow-[0_0_6px_rgba(250,204,21,0.8)] ml-1.5 align-middle" 
                        title="Filter modified"
                    ></span>
                )}
                <span
                    className="pvtTriangle"
                >
                    {' '}
                    &#x25BE;
                </span>
                {this.getFilterBox()}
            </div>
        );
    }
}

interface DropdownProps {
    zIndex?: number;
    toggle: () => void;
    open: boolean;
    current?: string;
    values: string[];
    setValue: (val: string) => void;
    disabled?: boolean;
}

export class Dropdown extends React.PureComponent<DropdownProps> {
    override render() {
        if (this.props.current === 'VOLUME') return null;
        return (
            <div className="pvtDropdown" style={{ zIndex: this.props.zIndex, opacity: this.props.disabled ? 0.6 : 1, cursor: this.props.disabled ? 'not-allowed' : 'auto' }}>
                <div
                    onClick={e => {
                        e.stopPropagation();
                        if (!this.props.disabled) {
                            this.props.toggle();
                        }
                    }}
                    className={
                        'pvtDropdownValue pvtDropdownCurrent ' +
                        (this.props.open ? 'pvtDropdownCurrentOpen' : '')
                    }
                    role="button"
                    style={this.props.disabled ? { pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' } : undefined}
                >
                    {!this.props.disabled && <div className="pvtDropdownIcon">{this.props.open ? '×' : '▾'}</div>}
                    {this.props.current === 'Table' ? <span className="text-[11px] font-semibold text-center mt-[1px]">Volume (lot)</span> : (this.props.current || <span>&nbsp;</span>)}
                </div>
                {this.props.open && (
                    <div className="pvtDropdownMenu">
                        {this.props.values.map((r: any) => (
                            <div
                                key={r}
                                role="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    if (this.props.current === r) {
                                        this.props.toggle();
                                    } else {
                                        this.props.setValue(r);
                                    }
                                }}
                                className={
                                    'pvtDropdownValue ' +
                                    (r === this.props.current ? 'pvtDropdownActiveValue' : '')
                                }
                            >
                                {r}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

interface PivotTableUIProps {
    data: any;
    onChange: (state: any) => void;
    renderers?: Record<string, any>;
    aggregators?: Record<string, any>;
    aggregatorName?: string;
    rendererName?: string;
    vals: string[];
    cols: string[];
    rows: string[];
    valueFilter: Record<string, Record<string, boolean>>;
    sorters?: any;
    derivedAttributes?: Record<string, (record: any) => any>;
    rowOrder?: string;
    colOrder?: string;
    hiddenAttributes?: string[];
    hiddenFromDragDrop?: string[];
    hiddenFromAggregators?: string[];
    menuLimit?: number | null;
    unusedOrientationCutoff?: number;
    sortDirs?: Record<string, 'asc' | 'desc' | 'none'>;
    rendererCellChild?: React.ReactNode;
    isRendererChildOpen?: boolean;
    activeMergeFields?: string[];
    onToggleMergeField?: (field: string, val: boolean) => void;
    [key: string]: any;
}

interface PivotTableUIState {
    unusedOrder: string[];
    zIndices: Record<string, number>;
    maxZIndex: number;
    openDropdown: string | false;
    attrValues: Record<string, Record<string, number>>;
    materializedInput: any[];
    groupByHour: boolean;
    isProcessing: boolean;
    sortDirs: Record<string, 'asc' | 'desc' | 'none'>;
}

class PivotTableUI extends React.PureComponent<PivotTableUIProps, PivotTableUIState> {
    static defaultProps = Object.assign({}, PivotData.defaultProps, {
        hiddenAttributes: [],
        hiddenFromDragDrop: [],
        hiddenFromAggregators: [],
        onDragDrop: () => { },
    });

    constructor(props: PivotTableUIProps) {
        super(props);
        this.state = {
            unusedOrder: [
                'BUYER', 'BO No', 'F/D BUY',
                'SELLER', 'SO No', 'F/D SELL',
                'VALUE', 'TIME', 'PRICE',
                'HAKA HAKI', 'SESSION', 'DATE'
            ],
            zIndices: {},
            maxZIndex: 500,
            openDropdown: false,
            attrValues: {},
            materializedInput: [],
            groupByHour: false,
            isProcessing: false,
            sortDirs: props.sortDirs || {
                PRICE: 'desc',
                TIME: 'desc',
                'SO No': 'asc',
                'BO No': 'asc',
            },
        };
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    override componentDidMount() {
        this.materializeInput(this.props);
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    override componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event: MouseEvent) {
        if (this.state.openDropdown) {
            const target = event.target as HTMLElement;
            const isInsideDropdown = target.closest('.pvtDropdown');
            if (!isInsideDropdown) {
                this.setState({ openDropdown: false });
            }
        }
    }

    override componentDidUpdate(prevProps: PivotTableUIProps, prevState: PivotTableUIState) {
        if (prevProps.data !== this.props.data || prevState.groupByHour !== this.state.groupByHour) {
            this.materializeInput(this.props);
        }
    }

    materializeInput(props: PivotTableUIProps) {
        const { data, derivedAttributes: rawDerived } = props;
        const derivedAttributes = rawDerived || {};

        const newState: any = {
            data,
            attrValues: {},
            materializedInput: [],
        };

        const skipIndexing = new Set([
            ...(this.props.hiddenAttributes || []),
            ...(this.props.hiddenFromDragDrop || []),
            ...(this.props.hiddenFromAggregators || [])
        ]);

        let recordsProcessed = 0;
        PivotData.forEachRecord(
            newState.data,
            {},
            (rawRecord: any) => {
                const record = { ...rawRecord };
                for (const k in derivedAttributes) {
                    const derived = derivedAttributes[k](record);
                    if (derived !== null) {
                        record[k] = derived;
                    }
                }

                const timeKey = record['TIME'] !== undefined ? 'TIME' : (record['TRX_TIME'] !== undefined ? 'TRX_TIME' : null);
                if (timeKey) {
                    const time = record[timeKey];
                    if (time !== null) {
                        // Assuming time is number or string like '093000' or 93000
                        // Convert to padded string '093000'
                        let t = String(time);
                        // Only format if it looks like raw format (no colons)
                        if (!t.includes(':')) {
                            t = t.padStart(6, '0');
                            if (this.state.groupByHour) {
                                record[timeKey] = t.substring(0, 2) + ':00';
                            } else {
                                record[timeKey] = `${t.substring(0, 2)}:${t.substring(2, 4)}:${t.substring(4, 6)}`;
                            }
                        } else if (this.state.groupByHour) {
                            // If already formatted (HH:mm:ss), just take hour
                            record[timeKey] = t.substring(0, 2) + ':00';
                        }
                    }
                }

                newState.materializedInput.push(record);
                for (const attr of Object.keys(record)) {
                    if (skipIndexing.has(attr)) continue;

                    if (!(attr in newState.attrValues)) {
                        newState.attrValues[attr] = {};
                        if (recordsProcessed > 0) {
                            newState.attrValues[attr].null = recordsProcessed;
                        }
                    }

                    const value = record[attr] ?? 'null';
                    if (!(value in newState.attrValues[attr])) {
                        newState.attrValues[attr][value] = 0;
                    }
                    newState.attrValues[attr][value]++;
                }
                recordsProcessed++;
            }
        );
        this.setState(newState);
    }

    sendPropUpdate(command: any) {
        this.setState({ isProcessing: true }, () => {
            setTimeout(() => {
                this.props.onChange(update(this.props, command));
                this.setState({ isProcessing: false });
            }, 50);
        });
    }

    propUpdater(key: string) {
        return (value: any) => this.sendPropUpdate({ [key]: { $set: value } });
    }

    setValuesInFilter(attribute: string, values: any[]) {
        this.sendPropUpdate({
            valueFilter: {
                [attribute]: {
                    $set: values.reduce((r, v) => {
                        r[v] = true;
                        return r;
                    }, {}),
                },
            },
        });
    }

    addValuesToFilter(attribute: string, values: any[]) {
        if (attribute in this.props.valueFilter) {
            const currentFilter = this.props.valueFilter[attribute] || {};
            const newFilter = { ...currentFilter };
            for (let i = 0; i < values.length; i++) {
                newFilter[values[i]] = true;
            }
            this.sendPropUpdate({
                valueFilter: {
                    [attribute]: {
                        $set: newFilter,
                    },
                },
            });
        } else {
            this.setValuesInFilter(attribute, values);
        }
    }

    removeValuesFromFilter(attribute: string, values: any[]) {
        if (attribute in this.props.valueFilter) {
            const currentFilter = this.props.valueFilter[attribute] || {};
            const newFilter = { ...currentFilter };
            for (let i = 0; i < values.length; i++) {
                delete newFilter[values[i]];
            }
            this.sendPropUpdate({
                valueFilter: {
                    [attribute]: {
                        $set: newFilter,
                    },
                },
            });
        }
    }

    moveFilterBoxToTop(attribute: string) {
        this.setState(
            update(this.state, {
                maxZIndex: { $set: this.state.maxZIndex + 1 },
                zIndices: { [attribute]: { $set: this.state.maxZIndex + 1 } },
            })
        );
    }

    isOpen(dropdown: any) {
        return this.state.openDropdown === dropdown;
    }

    makeDnDCell(items: any[], onChange: any, classes: string) {
        return (
            <SortableJSWrapper
                key={items.join(',')}
                options={{
                    group: 'shared',
                    ghostClass: 'pvtPlaceholder',
                    filter: '.pvtFilterBox',
                    preventOnFilter: false,
                }}
                tag="td"
                className={classes}
                onChange={onChange}
            >
                {items.map(x => (
                    <DraggableAttribute
                        name={x}
                        key={x}
                        attrValues={this.state.attrValues[x] || {}}
                        valueFilter={this.props.valueFilter[x] || {}}
                        sorter={getSort(this.props.sorters, x)}
                        menuLimit={this.props.menuLimit ?? null}
                        setValuesInFilter={this.setValuesInFilter.bind(this)}
                        addValuesToFilter={this.addValuesToFilter.bind(this)}
                        moveFilterBoxToTop={this.moveFilterBoxToTop.bind(this)}
                        removeValuesFromFilter={this.removeValuesFromFilter.bind(this)}
                        zIndex={this.state.zIndices[x] || this.state.maxZIndex}
                        groupByHour={x === 'TIME' ? (this.state.groupByHour ?? false) : undefined}
                        toggleGroupByHour={x === 'TIME' ? (val: boolean) => {
                            this.setState({ isProcessing: true }, () => {
                                setTimeout(() => {
                                    this.setState({ groupByHour: val, isProcessing: false });
                                }, 50);
                            });
                        } : undefined}
                        sortDir={this.state.sortDirs && this.state.sortDirs[x] ? this.state.sortDirs[x] : 'none'}
                        toggleSortDir={(dir: 'asc' | 'desc') => {
                            this.setState({ isProcessing: true }, () => {
                                setTimeout(() => {
                                    this.setState({
                                        sortDirs: {
                                            ...(this.state.sortDirs || {}),
                                            [x]: dir
                                        },
                                        isProcessing: false
                                    });
                                }, 50);
                            });
                        }}
                        isMergeActive={['BO No', 'SO No', 'PRICE', 'TIME', 'BOARD'].includes(x) ? this.props.activeMergeFields?.includes(x) : undefined}
                        toggleMerge={['BO No', 'SO No', 'PRICE', 'TIME', 'BOARD'].includes(x) ? ((val: boolean) => this.props.onToggleMergeField?.(x, val)) : undefined}
                    />
                ))}
            </SortableJSWrapper>
        );
    }

    override render() {
        const rawRenderers = this.props.renderers || PivotData.defaultProps.renderers;
        const rawAggregators = this.props.aggregators || PivotData.defaultProps.aggregators;

        const renderers = (normalize(rawRenderers) || {}) as Record<string, any>;
        const aggregators = (normalize(rawAggregators) || {}) as Record<string, any>;

        const aggregatorName =
            this.props.aggregatorName || PivotData.defaultProps.aggregatorName;
        const rendererName =
            this.props.rendererName || PivotData.defaultProps.rendererName;

        const rendererCell = (
            <td className="pvtRenderers" style={{ zIndex: (this.isOpen('renderer') || this.props.isRendererChildOpen) ? 2001 : 1, position: 'relative' }}>
                {this.props.rendererCellChild ? this.props.rendererCellChild : (
                    <Dropdown
                        current={rendererName}
                        values={Object.keys(renderers).filter(k => k !== 'default')}
                        open={this.isOpen('renderer')}
                        zIndex={this.state.maxZIndex + 1}
                        toggle={() =>
                            this.setState({
                                openDropdown: this.isOpen('renderer') ? false : 'renderer',
                            })
                        }
                        setValue={this.propUpdater('rendererName')}
                        disabled={true}
                    />
                )}
            </td>
        );

        const aggregatorCell = (
            <td className="pvtVals" style={{ zIndex: (this.isOpen('aggregator') || this.props.vals.some((x: any) => this.isOpen(`val${x}`))) ? 2001 : 1, position: 'relative' }}>
                <Dropdown
                    current={aggregatorName}
                    values={Object.keys(aggregators).filter(k => k !== 'default')}
                    open={this.isOpen('aggregator')}
                    zIndex={this.state.maxZIndex + 1}
                    toggle={() =>
                        this.setState({
                            openDropdown: this.isOpen('aggregator') ? false : 'aggregator',
                        })
                    }
                    setValue={this.propUpdater('aggregatorName')}
                />
                <br />
                {/* 
                    Dropdown for selecting which field to aggregate is hidden as per user request.
                    The aggregator will use the values passed in props.vals (defaulted to STK_VALUES).
                */}
            </td>
        );

        const strictUnusedOrder = [
            'BUYER', 'BO No', 'F/D BUY',
            'SELLER', 'SO No', 'F/D SELL',
            'VALUE', 'TIME', 'PRICE',
            'HAKA HAKI', 'SESSION', 'DATE'
        ];

        const unusedAttrsCell = this.makeDnDCell(
            Object.keys(this.state.attrValues)
                .filter(
                    x =>
                        !this.props.rows.includes(x) &&
                        !this.props.cols.includes(x) &&
                        !this.props.hiddenAttributes.includes(x) &&
                        !this.props.hiddenFromDragDrop.includes(x)
                )
                .sort(sortAs(strictUnusedOrder)),
            (order: any) => {
                // Force the unused order back to the fixed array to prevent arbitrary shuffling
                this.setState({ unusedOrder: strictUnusedOrder });
                // When moving to unused, ensure we remove from rows/cols if it came from there
                this.sendPropUpdate({
                    rows: { $set: this.props.rows.filter((x: any) => !order.includes(x)) },
                    cols: { $set: this.props.cols.filter((x: any) => !order.includes(x)) }
                });
            },
            'pvtAxisContainer pvtHorizList pvtUnused'
        );

        const colAttrsCell = this.makeDnDCell(
            this.props.cols.filter(
                (x: string) => !(this.props.hiddenAttributes || []).includes(x) && !(this.props.hiddenFromDragDrop || []).includes(x)
            ),
            (order: any) => this.sendPropUpdate({
                cols: { $set: order },
                rows: { $set: this.props.rows.filter((x: any) => !order.includes(x)) }
            }),
            'pvtAxisContainer pvtHorizList pvtCols'
        );

        const rowAttrsCell = this.makeDnDCell(
            this.props.rows.filter(
                (x: string) => !(this.props.hiddenAttributes || []).includes(x) && !(this.props.hiddenFromDragDrop || []).includes(x)
            ),
            (order: any) => this.sendPropUpdate({
                rows: { $set: order },
                cols: { $set: this.props.cols.filter((x: any) => !order.includes(x)) }
            }),
            'pvtAxisContainer pvtRows'
        );

        const outputCell = (
            <td className="pvtOutput" style={{ position: 'relative', width: '100%', height: '100%' }}>
                {this.state.isProcessing && (
                    <div className="absolute inset-0 z-[4000] flex items-center justify-center bg-[#0a0f20]/60 backdrop-blur-[2px] transition-all">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-white" />
                            <span className="text-sm font-medium text-white/80">Recalculating...</span>
                        </div>
                    </div>
                )}
                <div style={{ overflow: 'hidden', width: '100%', height: '100%', opacity: this.state.isProcessing ? 0.5 : 1 }}>
                    <PivotTableComponent
                        {...update(this.props, {
                            data: { $set: this.state.materializedInput },
                            derivedAttributes: { $set: {} },
                            renderers: { $set: renderers },
                            aggregators: { $set: aggregators },
                            sorters: {
                                $set: {
                                    ...this.props.sorters,
                                    ...Object.entries(this.props.sortDirs || this.state.sortDirs).reduce((acc: any, [attr, dir]) => {
                                        if (dir === 'none') return acc;
                                        
                                        // Specific numeric attributes
                                        const isNumeric = ['PRICE', 'VOLUME', 'VALUE', 'SO No', 'BO No'].includes(attr);
                                        
                                        acc[attr] = (a: any, b: any) => {
                                            if (dir === 'asc') {
                                                if (isNumeric) return parseFloat(a) - parseFloat(b);
                                                return String(a).localeCompare(String(b));
                                            } else {
                                                if (isNumeric) return parseFloat(b) - parseFloat(a);
                                                return String(b).localeCompare(String(a));
                                            }
                                        };
                                        return acc;
                                    }, {})
                                }
                            },
                            tableOptions: {
                                $set: {
                                    ...(this.props.tableOptions || {}),
                                    onHeaderClick: (attr: string) => {
                                        const isTotals = attr === 'Totals' || attr === 'Totals_Cols';
                                        const sortDirs = this.props.sortDirs || this.state.sortDirs;
                                        const currentDir = sortDirs[attr] || 'none';
                                        let nextDir: 'asc' | 'desc' | 'none' = 'asc';
                                        
                                        // Specific cycle for Totals: desc -> asc -> none
                                        if (isTotals) {
                                            if (currentDir === 'none') nextDir = 'desc';
                                            else if (currentDir === 'desc') nextDir = 'asc';
                                            else nextDir = 'none';
                                        } else {
                                            if (currentDir === 'asc') nextDir = 'desc';
                                            else if (currentDir === 'desc') nextDir = 'none';
                                        }

                                        this.setState({ isProcessing: true }, () => {
                                            setTimeout(() => {
                                                const newSortDirs: any = { [attr]: nextDir };
                                                if (attr === 'Totals' && sortDirs.Totals_Cols) {
                                                    newSortDirs['Totals_Cols'] = sortDirs.Totals_Cols;
                                                }
                                                if (attr === 'Totals_Cols' && sortDirs.Totals) {
                                                    newSortDirs['Totals'] = sortDirs.Totals;
                                                }

                                                if (this.props.onChange) {
                                                    this.props.onChange({
                                                        ...this.props,
                                                        sortDirs: newSortDirs
                                                    });
                                                }

                                                this.setState({
                                                    sortDirs: newSortDirs,
                                                    isProcessing: false
                                                });
                                            }, 50);
                                        });
                                    },
                                    sortDirs: this.props.sortDirs || this.state.sortDirs,
                                    prevClose: this.props.prevClose
                                }
                            },
                            rowOrder: {
                                $set: (this.props.sortDirs || this.state.sortDirs).Totals === 'asc' ? 'value_a_to_z' : 
                                    ((this.props.sortDirs || this.state.sortDirs).Totals === 'desc' ? 'value_z_to_a' : (this.props.rowOrder || 'key_a_to_z'))
                            },
                            colOrder: {
                                $set: (this.props.sortDirs || this.state.sortDirs).Totals_Cols === 'asc' ? 'value_a_to_z' : 
                                    ((this.props.sortDirs || this.state.sortDirs).Totals_Cols === 'desc' ? 'value_z_to_a' : (this.props.colOrder || 'key_a_to_z'))
                            }
                        })}
                    />
                </div>
            </td>
        );

        return (
            <table className="pvtUi">
                <thead>
                    <tr>
                        {rendererCell}
                        {unusedAttrsCell}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {aggregatorCell}
                        {colAttrsCell}
                    </tr>
                    <tr>
                        {rowAttrsCell}
                        {outputCell}
                    </tr>
                </tbody>
            </table>
        );
    }
}

(PivotTableUI as any).propTypes = Object.assign({}, PivotData.propTypes, {
    onChange: PropTypes.func.isRequired,
    hiddenAttributes: PropTypes.arrayOf(PropTypes.string),
    hiddenFromDragDrop: PropTypes.arrayOf(PropTypes.string),
    hiddenFromAggregators: PropTypes.arrayOf(PropTypes.string),
    onDragDrop: PropTypes.func,
});

export default PivotTableUI;
